import { faFilter } from '@fortawesome/pro-light-svg-icons/faFilter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Checkbox from 'components/common/checkbox';
import connect from 'components/lib/connect';
import ConversationHistoryFilter from 'models/conversation_history_filter';
import Dropdown from 'components/lib/dropdown';
import getNormalizedEmailAddress from 'scripts/lib/get_normalized_email_address';
import OutsideClickHandler from 'components/common/utilities/outside_click_handler';
import SetConversationHistoryFilter from 'actions/customer/feed/set_conversation_history_filter';
import TYPE_GROUPS, { groupLabel } from './filtering/conversation_item_type_groups';

const ALL_ADDRESSES = '__ALL__';

export function ConversationHistoryFilterBar({ addressOptions, filter, setFilter }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Filter reset on customer change is handled by ConversationHistory (see the
  // customerId-keyed effect there), not here — this component unmounts on
  // loading flickers for the same customer, which would wipe an active filter.

  const activeCount = filter.hiddenTypeGroups.length + (filter.address ? 1 : 0);

  const sortedGroups = [...TYPE_GROUPS].sort((a, b) => groupLabel(t, a.key).localeCompare(groupLabel(t, b.key)));

  const toggleGroup = groupKey => {
    const hiddenTypeGroups = filter.isTypeGroupHidden(groupKey)
      ? filter.hiddenTypeGroups.filter(key => key !== groupKey)
      : [...filter.hiddenTypeGroups, groupKey];
    setFilter(ConversationHistoryFilter.create({ address: filter.address, hiddenTypeGroups }));
  };

  const changeAddress = option => {
    const address = option.value === ALL_ADDRESSES ? '' : option.value;
    setFilter(ConversationHistoryFilter.create({ address, hiddenTypeGroups: filter.hiddenTypeGroups }));
  };

  const clear = () => setFilter(ConversationHistoryFilter.create());

  return (
    <OutsideClickHandler onClickOutside={() => setIsOpen(false)}>
      <Container>
        <ToggleButton
          active={activeCount > 0}
          data-aid="conversationFilter-toggle"
          onClick={() => setIsOpen(open => !open)}
        >
          <FilterIcon icon={faFilter} />
          {t('Filter')}
          {activeCount > 0 ? ` (${activeCount})` : ''}
        </ToggleButton>
        {activeCount > 0 && (
          <ClearButton data-aid="conversationFilter-clear" onClick={clear}>
            {t('Clear')}
          </ClearButton>
        )}
        {isOpen && (
          <Panel data-aid="conversationFilter-panel">
            <Section>
              <SectionTitle>{t('Show item types')}</SectionTitle>
              {sortedGroups.map(group => (
                <CheckboxRow key={group.key}>
                  <Checkbox
                    checked={!filter.isTypeGroupHidden(group.key)}
                    data-aid={`conversationFilter-type-${group.key}`}
                    label={groupLabel(t, group.key)}
                    onChange={() => toggleGroup(group.key)}
                  />
                </CheckboxRow>
              ))}
            </Section>
            {addressOptions.length > 0 && (
              <Section>
                <SectionTitle>{t('Endpoint address')}</SectionTitle>
                <Dropdown
                  onChange={changeAddress}
                  options={[{ label: t('All addresses'), value: ALL_ADDRESSES }, ...addressOptions]}
                  value={filter.address || ALL_ADDRESSES}
                />
              </Section>
            )}
          </Panel>
        )}
      </Container>
    </OutsideClickHandler>
  );
}

ConversationHistoryFilterBar.propTypes = {
  addressOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  filter: PropTypes.object.isRequired,
  setFilter: PropTypes.func.isRequired,
};

function buildAddressOptions(profile) {
  if (!profile) {
    return [];
  }

  const options = [];
  const seen = new Set();

  (profile.emails || []).forEach(email => {
    const value = getNormalizedEmailAddress(email.original);
    if (value && !seen.has(value)) {
      seen.add(value);
      options.push({ label: email.original, value });
    }
  });

  (profile.phones || []).forEach(phone => {
    const value = phone.normalized;
    if (value && !seen.has(value)) {
      seen.add(value);
      options.push({ label: phone.formatForDisplay(), value });
    }
  });

  return options;
}

function mapStateToProps({ getProvider }) {
  return {
    addressOptions: buildAddressOptions(getProvider('profile').get()),
    filter: getProvider('conversationHistoryFilter').get(),
  };
}

function mapExecuteToProps(executeAction) {
  return {
    setFilter: filter => executeAction(SetConversationHistoryFilter, filter),
  };
}

const Container = styled.div`
  left: 8px;
  position: absolute;
  top: 11px;
  z-index: 6;
`;

const ToggleButton = styled.button`
  background: ${p => (p.active ? p.theme.colors.green400 : p.theme.colors.white)};
  border: 1px solid ${p => (p.active ? p.theme.colors.green400 : p.theme.colors.gray400)};
  border-radius: 16px;
  color: ${p => (p.active ? p.theme.colors.white : p.theme.colors.gray800)};
  cursor: pointer;
  font-size: ${p => p.theme.fontSize.small};
  font-weight: ${p => p.theme.fontWeight.medium};
  padding: 4px 12px;
`;

const FilterIcon = styled(FontAwesomeIcon)`
  margin-right: 6px;
`;

const ClearButton = styled.button`
  background: transparent;
  border: none;
  color: ${p => p.theme.colors.gray600};
  cursor: pointer;
  font-size: ${p => p.theme.fontSize.small};
  margin-left: 4px;
  padding: 4px 8px;
`;

const Panel = styled.div`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: 8px;
  box-shadow: ${p => p.theme.boxShadow.medium};
  margin-top: 6px;
  min-width: 240px;
  padding: 12px 16px;
`;

const Section = styled.div`
  & + & {
    border-top: 1px solid ${p => p.theme.colors.gray200};
    margin-top: 12px;
    padding-top: 12px;
  }
`;

const SectionTitle = styled.div`
  color: ${p => p.theme.colors.gray600};
  font-size: ${p => p.theme.fontSize.small};
  font-weight: ${p => p.theme.fontWeight.medium};
  margin-bottom: 8px;
  text-transform: uppercase;
`;

const CheckboxRow = styled.div`
  margin-bottom: 6px;
`;

export default connect(mapStateToProps, mapExecuteToProps)(ConversationHistoryFilterBar);
